import { resolveFieldDisplayStyle, type FieldDef } from '@citizen-walk/shared-types';

/**
 * 参加セッションの選択 UI (2026-09-05)。
 *
 * `sessions` 項目は選択肢が地区のセッション一覧 (`districts/{id}/sessions`) から来るため、
 * `UserFieldsForm` の汎用レンダリング (field.options 駆動) では描けず、親が
 * `renderSessions` で差し込む特殊扱いになっている。そのため**管理画面の項目設定が
 * どこにも反映されていなかった**(常に複数選択のチェックボックス / 見出しも固定文言)。
 * オーナー報告 2026-09-05:「択一にしているのに択一にならない、表示タイプをボタンにしても
 * チェックボックスのまま」。
 *
 * 本コンポーネントが `sessions` の FieldDef を読み、他の項目と同じ規則で描画する:
 *   - `type`         : `select` = 択一 / `multiselect` = 複数選択 (既定)
 *   - `displayStyle` : buttons / radio / checkboxes / dropdown (型ごとの許可は shared-types 側)
 *   - `label`        : 見出し (「参加セッション・グループ」等の地区独自名を尊重)
 *
 * 択一に切り替えても既存の複数参加を勝手に削らない (オーナー判断 2026-09-05)。
 * 画面は択一として振る舞い、本人が選び直して保存した時点で 1 つになる。
 */

export interface SessionPickerItem {
  id: string;
  name: string;
}

export interface SessionPickerProps {
  sessions: SessionPickerItem[];
  /** 地区の `sessions` 項目定義。未指定なら従来どおり複数選択のチェックボックス。 */
  field?: FieldDef | null;
  /** 選択中のセッション ID (複数選択でなくても配列で扱う = 保存先 joinedSessionIds に合わせる)。 */
  value: string[];
  onChange: (next: string[]) => void;
  disabled?: boolean;
  /** 見出しを描画するか。親が Section 見出しを出す場合は false。 */
  showLabel?: boolean;
}

/** 択一かどうか (field 未指定 / multiselect は複数選択)。 */
export function isSingleSelectSessions(field?: FieldDef | null): boolean {
  return field?.type === 'select';
}

/** 実際に使う表示スタイル (field 未指定なら従来の checkboxes)。 */
export function resolveSessionDisplayStyle(field?: FieldDef | null): string {
  if (!field) return 'checkboxes';
  return resolveFieldDisplayStyle(field);
}

export function SessionPicker({
  sessions,
  field,
  value,
  onChange,
  disabled = false,
  showLabel = false,
}: SessionPickerProps) {
  if (sessions.length === 0) return null;
  const single = isSingleSelectSessions(field);
  const style = resolveSessionDisplayStyle(field);
  const label = field?.label?.trim() || '参加セッション';
  const required = field?.mode === 'required';

  /** 択一は選び直しで置き換え (同じものを再度押すと解除)。複数選択は従来どおり追加/削除。 */
  function toggle(id: string) {
    if (single) {
      onChange(value.includes(id) && value.length === 1 ? [] : [id]);
      return;
    }
    onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);
  }

  const hint = single
    ? '参加するセッションを 1 つ選んでください。'
    : '参加するセッションを選んでください（複数選択できます）。';

  const labelNode = showLabel ? (
    <label className="block mb-1 font-medium text-sm">
      {label} {required && <span className="text-danger-600">*</span>}
    </label>
  ) : null;

  // ボタン: 択一は radiogroup、複数選択は押すたびに on/off するトグル群。
  if (style === 'buttons') {
    return (
      <div
        data-testid="session-picker"
        data-mode={single ? 'single' : 'multi'}
        data-style="buttons"
      >
        {labelNode}
        <p className="text-xs text-slate-500 mb-1">{hint}</p>
        <div role={single ? 'radiogroup' : 'group'} className="flex flex-wrap items-center gap-2">
          {sessions.map((s) => {
            const active = value.includes(s.id);
            return (
              <button
                key={s.id}
                type="button"
                {...(single
                  ? { role: 'radio' as const, 'aria-checked': active }
                  : { 'aria-pressed': active })}
                disabled={disabled}
                onClick={() => toggle(s.id)}
                className={`min-h-touch px-3 rounded-md border text-sm font-medium transition-colors disabled:opacity-50 ${
                  active
                    ? 'bg-primary-600 text-white border-primary-600'
                    : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                }`}
              >
                {s.name}
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  // ドロップダウン: 択一のみ (複数選択は下のチェックボックスに落とす)。
  if (style === 'dropdown' && single) {
    return (
      <div data-testid="session-picker" data-mode="single" data-style="dropdown">
        {labelNode}
        <p className="text-xs text-slate-500 mb-1">{hint}</p>
        <select
          value={value[0] ?? ''}
          onChange={(e) => onChange(e.target.value ? [e.target.value] : [])}
          disabled={disabled}
          aria-label={label}
          className="w-full min-h-touch px-3 border border-slate-300 rounded-md text-sm bg-white"
        >
          <option value="">— 選択してください —</option>
          {sessions.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
      </div>
    );
  }

  // 既定: 択一はラジオ、複数選択はチェックボックス (従来の見た目)。
  return (
    <div
      data-testid="session-picker"
      data-mode={single ? 'single' : 'multi'}
      data-style={single ? 'radio' : 'checkboxes'}
    >
      {labelNode}
      <p className="text-xs text-slate-500 mb-1">{hint}</p>
      <div className="grid gap-1">
        {sessions.map((s) => {
          const active = value.includes(s.id);
          return (
            <label key={s.id} className="flex items-center gap-2 py-2">
              <input
                type={single ? 'radio' : 'checkbox'}
                {...(single ? { name: 'session-picker' } : {})}
                checked={active}
                onChange={() => toggle(s.id)}
                disabled={disabled}
                className="w-5 h-5"
              />
              <span className="text-sm">{s.name}</span>
            </label>
          );
        })}
      </div>
    </div>
  );
}
